import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, switchMap, tap } from 'rxjs';
import { composePath } from 'src/helpers/compose-path';
import { LangService } from 'src/helpers/lang-service';

import reportSentryError from '../../helpers/reportSentryError';
import { ApiService } from '../api/api.service';
import { LoaderService } from '../components/loader/loader.service';
import { ToastService } from '../components/toast/toast.service';
import { UserService } from '../services/user.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  isUserAuthorized = computed(() => this.isAuthorized());
  //Value that indicates whether the page is loaded after the user authentication
  //or just simpler action ex. page refresh. During page init it is necessary to
  //trigger checkIfauthorize inside getAccessToken function. During simple
  //page refresh we still need to check if user is authorized and
  //initialize particular page, but without need for new access token.
  //We needed to create a flag that stops check from triggering twice
  readonly isAuthFlow = signal(false);
  private readonly httpClient = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly apiService = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly isAuthorized = signal(false);
  private readonly langService = inject(LangService);
  private readonly userService = inject(UserService);

  checkIfUserAuthorized() {
    this.loaderService.setLoader(true);

    return this.httpClient
      .get(`${this.apiService.createBffUrl({ path: 'auth', useRegion: true })}/check`, {
        withCredentials: true,
      })

      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.userService.fetchUserData()),
        tap(() => {
          this.isAuthorized.set(true);
          this.langService.redirectToPage();
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 500) {
            reportSentryError({
              error,
              customErrorName: 'Check User Authorized Error',
              endpointName: 'check-user-authorized',
            });
          }

          this.isAuthorized.set(false);
          this.router.navigate([composePath('/auth')]);

          return EMPTY;
        }),
        finalize(() => {
          this.loaderService.setLoader(false);
        }),
      );
  }

  getAccessToken(authorizationCode: string) {
    this.isAuthFlow.set(true);
    this.loaderService.setLoader(true);

    return this.httpClient
      .post(
        `${this.apiService.createBffUrl({ path: 'auth', useRegion: true })}/token?authorizationCode=${authorizationCode}`,
        {},
        { withCredentials: true },
      )
      .pipe(
        switchMap(() => this.checkIfUserAuthorized()),
        catchError((error: HttpErrorResponse) => {
          reportSentryError({
            error,
            customErrorName: 'Get Access Token Error',
            endpointName: 'get-access-token',
          });

          return EMPTY;
        }),
        finalize(() => {
          this.isAuthFlow.set(false);
          this.loaderService.setLoader(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      );
  }

  logout() {
    const subscription = this.httpClient
      .post(`${this.apiService.createBffUrl({ path: 'auth', useRegion: false })}/logout`, {}, { withCredentials: true })
      .subscribe({
        next: () => {
          this.isAuthorized.set(false);
          this.router.navigate([composePath('/auth')], { queryParams: { logout: true } });
        },
        error: (error) => {
          reportSentryError({
            error,
            customErrorName: 'Logout Error',
            endpointName: 'logout',
          });
          this.toast.showError('something_went_wrong');
          console.error({ error });
        },
      });

    this.destroyRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }
}
